import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircleMore, TentTree } from "lucide-react";
import { XStack, YStack } from "tamagui";

import AppStoreBadge from "@/components/AppStoreBadge";
import {
  PublicBrandCard,
  PublicMarketingFooter,
  PublicMarketingPage,
} from "@/components/PublicMarketingScaffold";
import {
  buildWebAuthPath,
  rememberWebPostAuthRedirect,
} from "@/lib/postAuthRedirect";
import {
  CAMPING_BREAKS_ROOM_PATH,
} from "../../shared/community/campaigns";
import { KINDRED_SUPPORT_EMAIL } from "../../shared/support";
import {
  AppButton,
  AppContentContainer,
  AppHeading,
  AppText,
  kindredFontFamilies,
} from "../../ui-foundation";

const APP_SCHEME_ROOM_URL = `kindredcommunity://${CAMPING_BREAKS_ROOM_PATH.slice(1)}`;

export default function CampingBreaksInvite() {
  const navigate = useNavigate();

  useEffect(() => {
    rememberWebPostAuthRedirect(CAMPING_BREAKS_ROOM_PATH);
  }, []);

  return (
    <PublicMarketingPage>
      <section className="relative min-h-[min(720px,calc(100svh-72px))] overflow-hidden">
        <img
          src="/social/camping-breaks-hero.jpg"
          alt="Single-parent families setting up tents together beside a lake"
          className="absolute inset-0 h-full w-full object-cover object-[62%_center]"
          loading="eager"
        />
        <div className="absolute inset-0 bg-black/50" aria-hidden />
        <AppContentContainer
          position="relative"
          minHeight="min(720px, calc(100svh - 72px))"
          justifyContent="space-between"
          py="$lg"
          gap="$xl"
        >
          <XStack>
            <PublicBrandCard />
          </XStack>

          <YStack gap="$lg" alignItems="flex-start" maxWidth={660} pb="$xl">
            <YStack gap="$sm">
              <XStack alignItems="center" gap="$sm">
                <TentTree size={22} color="white" aria-hidden />
                <AppText
                  fontFamily={kindredFontFamilies.sansBold}
                  fontSize="$xs"
                  lineHeight="$sm"
                  color="white"
                  textTransform="uppercase"
                  letterSpacing={1.4}
                >
                  Summer plans with other single-parent families
                </AppText>
              </XStack>
              <AppHeading
                color="white"
                fontSize={48}
                lineHeight={54}
                letterSpacing={0}
                $gtSm={{ fontSize: 64, lineHeight: 70 }}
              >
                Camping &amp; Breaks
              </AppHeading>
              <AppText fontSize="$lg" lineHeight="$lg" color="white" maxWidth={620}>
                Join the Kindred chat for campsite ideas, family breaks, days by the water, and making plans with
                other single parents.
              </AppText>
            </YStack>

            <YStack width="100%" maxWidth={430} gap="$sm">
              <AppButton
                size="lg"
                onPress={() => navigate(buildWebAuthPath("/signup", CAMPING_BREAKS_ROOM_PATH))}
              >
                Join the Camping &amp; Breaks chat
              </AppButton>
              <AppButton
                size="lg"
                tone="outline"
                backgroundColor="$cardBackground"
                onPress={() => {
                  window.location.href = APP_SCHEME_ROOM_URL;
                }}
              >
                <MessageCircleMore size={18} aria-hidden />
                Open in Kindred
              </AppButton>
              <AppButton
                tone="outline"
                color="white"
                borderColor="rgba(255,255,255,0.72)"
                backgroundColor="rgba(18,20,24,0.62)"
                onPress={() => navigate(buildWebAuthPath("/signin", CAMPING_BREAKS_ROOM_PATH))}
              >
                Already a member? Sign in
              </AppButton>
            </YStack>
          </YStack>
        </AppContentContainer>
      </section>
      <section className="bg-background py-8">
        <AppContentContainer alignItems="center" gap="$sm">
          <AppStoreBadge loading="eager" />
          <AppText color="$colorMuted" fontSize="$xs" lineHeight="$sm" maxWidth={580} textAlign="center">
            Android is still in Google Play testing.{" "}
            <a
              className="kindred-inline-link"
              href={`mailto:${KINDRED_SUPPORT_EMAIL}?subject=${encodeURIComponent("Camping & Breaks Android access")}&body=${encodeURIComponent("Please add this Google Play email address to the Kindred Android test:\n\n")}`}
            >
              Request Android access
            </a>{" "}
            and include the email address you use for Google Play. You can send the request from any email address.
          </AppText>
        </AppContentContainer>
      </section>
      <PublicMarketingFooter />
    </PublicMarketingPage>
  );
}
